import React from 'react'
import styled, { css } from 'styled-components'
import uniqueId from 'lodash/fp/uniqueId'
import { StyledText } from 'components/styled/typography'
import { BodyStyle } from '../typography/styles'

export interface RadioProps {
  onChange: (value: string) => void
  value?: string
  isSelected?: boolean
  disabled?: boolean
  name?: string
  id?: string
  label?: string | React.ReactNode
}

const PrimaryFocusStyle = css`
  box-shadow: 0 0 0 1px ${({ theme }) => theme.color.white},
    0 0 0 3px ${({ theme }) => theme.color.primary70};
`

const EnabledSelectedDivStyle = css`
  background-color: ${({ theme }) => theme.color.primary110};
  border: 1px solid ${({ theme }) => theme.color.primary110};

  &:hover {
    background-color: ${({ theme }) => theme.color.primary120};
    border: 1px solid ${({ theme }) => theme.color.primary120};
  }

  &:active {
    background-color: ${({ theme }) => theme.color.primary130};
    border: 1px solid ${({ theme }) => theme.color.primary130};
  }
`

const DisabledSelectedDivStyle = css`
  background-color: ${({ theme }) => theme.color.primary30};
  border: 1px solid ${({ theme }) => theme.color.primary30};
`

const StyledSelectedDiv = styled.div<{ $disabled: boolean }>`
  width: ${({ theme }) => theme.space.s8};
  height: ${({ theme }) => theme.space.s8};
  border-radius: ${({ theme }) => theme.space.s8};

  ${({ $disabled }) =>
    $disabled ? DisabledSelectedDivStyle : EnabledSelectedDivStyle}
`

const SelectedRadioStyle = css<{ $isSelected: boolean }>`
  border-color: ${({ theme }) => theme.color.primary110};

  &:hover {
    border-color: ${({ theme }) => theme.color.primary120};
  }

  &:active {
    border-color: ${({ theme }) => theme.color.primary130};
  }
`

const UnSelectedRadioStyle = css<{ $isSelected: boolean }>`
  border-color: ${({ theme }) => theme.color.grey90};

  &:hover {
    border-color: ${({ theme }) => theme.color.grey100};
  }

  &:active {
    border-color: ${({ theme }) => theme.color.grey100};
  }
`

const DisabledRadioStyle = css<{ $isSelected: boolean }>`
  border-color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.color.primary30 : theme.color.grey30};
`

const EnabledRadioStyle = css<{ $isSelected: boolean }>`
  &.focus-visible {
    ${PrimaryFocusStyle};
  }

  ${({ $isSelected }) =>
    $isSelected ? SelectedRadioStyle : UnSelectedRadioStyle};
`

export const RadioLabel = styled.label<{ $disabled?: boolean }>`
  ${BodyStyle};
  line-height: ${({ theme }) => theme.space.s20};
  padding: 0 ${({ theme }) => theme.space.s16};
  user-select: none;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};

  ${StyledText} {
    color: inherit;
  }
`

export const StyledRadio = styled.div<{ $isSelected: boolean; $disabled: boolean }>`
  display: flex;
  flex-direction: column;
  user-select: none;
  outline: none;
  justify-content: center;
  align-items: center;
  width: ${({ theme }) => theme.space.s20};
  height: ${({ theme }) => theme.space.s20};
  border: ${({ theme }) => theme.space.s2} solid;
  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.space.s20};
  flex-shrink: 0;

  ${({ $disabled }) => ($disabled ? DisabledRadioStyle : EnabledRadioStyle)};
`

export const RadioWrapper = styled.div<{ $disabled: boolean }>`
  display: flex;
  align-items: flex-start;
  width: fit-content;
  outline: none;
  margin: ${({ theme }) => theme.space.s8} 0;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};

  &.focus-visible > div {
    ${({ $disabled }) => !$disabled && PrimaryFocusStyle};
  }
`
export const HiddenInput = styled.input<{ $disabled: boolean }>`
  border: 0px;
  clip: rect(0px, 0px, 0px, 0px);
  height: 1px;
  width: 1px;
  margin: -1px;
  padding: 0px;
  overflow: hidden;
  white-space: nowrap;
  position: absolute;

  &.focus-visible + div {
    ${({ $disabled }) => !$disabled && PrimaryFocusStyle};
  }
`

export const Radio: React.FC<RadioProps> = ({
  id,
  value = '',
  isSelected = false,
  disabled = false,
  label,
  onChange,
  ...props
}) => {
  const radioId = id || uniqueId('radio-')
  return (
    <RadioWrapper {...props} $disabled={disabled}>
      <HiddenInput
        type="radio"
        data-testid="hidden-input"
        value={value}
        $disabled={disabled}
        checked={isSelected}
        id={radioId}
        onChange={() => !disabled && onChange(value)}
      />
      <StyledRadio
        $isSelected={isSelected}
        $disabled={disabled}
        onClick={() => !disabled && onChange(value)}
        data-testid="radio-styled"
      >
        {isSelected && <StyledSelectedDiv $disabled={disabled} />}
      </StyledRadio>
      {label && (
        <RadioLabel $disabled={disabled} htmlFor={radioId}>
          {label}
        </RadioLabel>
      )}
    </RadioWrapper>
  )
}
